/*------------------------------------------------------------------------------
   About      : Shared fetch of the insight catalog (epic DA-697, II-FE-1)

                Cached at module scope so the list screen, the create form, the
                edit form, the criteria builder and the preview pane share one
                fetch instead of one each — the same shape as
                `mapperBuilder/useMapperCatalog.ts`, which this follows
                deliberately.

                Server-side the catalog is Redis-cached for 300s and relayed
                verbatim by proxypulse, so it is safe to treat as stable for a
                session. Use `resetInsightCatalogCache` after a registry edit.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { useEffect, useRef, useState } from 'react'
import { RunContextApp }               from 'framework/RCApp'
import { InsightApis }                 from '../api/insightAPI'
import { InsightCatalog }              from '../types/insightTypes'

let catalogPromise : Promise<InsightCatalog> | null = null

function fetchCatalog(rc: RunContextApp): Promise<InsightCatalog> {
  if (!catalogPromise) {
    catalogPromise = InsightApis.getCatalog(rc).then((resp) => {
      if (resp.error || !resp.data) {
        // Let the next attempt retry instead of caching a failure — otherwise a
        // single transient error poisons every catalog consumer for the whole
        // session, and the user's only recovery is a full reload.
        catalogPromise = null
        throw new Error(resp.error?.message || 'Failed to load insight catalog')
      }
      return resp.data
    })
  }
  return catalogPromise
}

/**
 * Drops the cached catalog so the next consumer refetches.
 *
 * Needed because `derivedQuantities` is real data (quill's
 * `derived-quantity-variable` table, DA-2366), not a static map — an admin edit
 * there changes what tokens are offered and what validates, and DA-2366
 * requires those edits to be audited precisely because they change validation
 * behaviour. A session holding a stale catalog would offer tokens the server has
 * since rejected.
 */
export function resetInsightCatalogCache() : void {
  catalogPromise = null
}

export interface UseInsightCatalogState {
  catalog ?: InsightCatalog
  loading  : boolean
  error   ?: string
}

export function useInsightCatalog(_rc: RunContextApp): UseInsightCatalogState {
  const [state, setState] = useState<UseInsightCatalogState>({ loading : true })

  // Held in a ref rather than as an effect dependency, for the same reason
  // useMapperCatalog does it: the catalog is global and module-cached, so there
  // is nothing to refetch when `rc` changes — and depending on its identity
  // means any caller whose context value is not memoized re-runs the effect
  // every render, each run setting fresh state and triggering the next render.
  // That is an infinite loop, and it is not the caller's job to know that.
  const rcRef = useRef(_rc)
  rcRef.current = _rc

  useEffect(() => {
    let cancelled = false

    fetchCatalog(rcRef.current)
      .then((catalog) => {
        if (!cancelled) setState({ catalog, loading : false })
      })
      .catch((err) => {
        if (!cancelled) {
          setState({ loading : false, error : err?.message || 'Failed to load insight catalog' })
        }
      })

    return () => { cancelled = true }
  }, [])

  return state
}
